import {
  type RovoContentBridgeAPI,
  type client,
} from '@atlassian/rovo-content-bridge-api';

import {
  KeyboardDownCommand,
  KeyboardUpCommand,
} from '@canvas/databases-bridge-api/commands';

import { getElementFromEventTarget, isInteractiveElement } from './utils.ts';

type ForwardKeyboardEventsParams = {
  bridge: RovoContentBridgeAPI;
  localClientId: ReturnType<typeof client>;
  externalClientId: ReturnType<typeof client>;
};

export function setupKeyboardEventForwarding({
  bridge,
  localClientId,
  externalClientId,
}: ForwardKeyboardEventsParams): () => void {
  const handleKeyDown = (event: KeyboardEvent) => {
    const target = getElementFromEventTarget(event.target);
    if (!target) {
      return;
    }

    const isInteractiveDbElement = isInteractiveElement(event);

    bridge.send(KeyboardDownCommand, {
      senderId: localClientId,
      receiverId: externalClientId,
      ...createKeyboardEventProperties(event, isInteractiveDbElement),
    });
  };

  const handleKeyUp = (event: KeyboardEvent) => {
    const target = getElementFromEventTarget(event.target);
    if (!target) {
      return;
    }

    const isInteractiveDbElement = isInteractiveElement(event);

    bridge.send(KeyboardUpCommand, {
      senderId: localClientId,
      receiverId: externalClientId,
      ...createKeyboardEventProperties(event, isInteractiveDbElement),
    });
  };

  const eventOptions = {
    passive: false,
    capture: true,
  };

  document.addEventListener('keydown', handleKeyDown, eventOptions);
  document.addEventListener('keyup', handleKeyUp, eventOptions);

  return () => {
    document.removeEventListener('keydown', handleKeyDown, eventOptions);
    document.removeEventListener('keyup', handleKeyUp, eventOptions);
  };
}

const createKeyboardEventProperties = (
  event: KeyboardEvent,
  isInteractiveDbElement = false,
) => ({
  key: event.key,
  code: event.code,
  ctrlKey: event.ctrlKey,
  shiftKey: event.shiftKey,
  altKey: event.altKey,
  metaKey: event.metaKey,
  isInteractiveDbElement,
});
